import { Controller } from "@hotwired/stimulus"

/**
 * District Autocomplete Controller
 *
 * Provides search autocomplete for Taiwan districts with trending display.
 * Integrates with region-map controller for map navigation.
 *
 * Features:
 * - Debounced API search
 * - Trending districts display
 * - Keyboard navigation (up/down/enter/escape)
 * - Map integration via custom events
 *
 * Usage:
 * <div data-controller="district-autocomplete"
 *      data-district-autocomplete-search-url-value="/api/v1/districts/search"
 *      data-district-autocomplete-debounce-value="300">
 */
export default class extends Controller {
  static targets = [
    "input",
    "clearButton",
    "dropdown",
    "loading",
    "trendingSection",
    "trendingList",
    "resultsList",
    "noResults"
  ]

  static values = {
    searchUrl: String,
    trendingUrl: String,
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this.selectedIndex = -1
    this.results = []
    this.trendingDistricts = []
    this.abortController = null

    // Fetch trending districts on connect
    this.fetchTrendingDistricts()

    // Hide dropdown on map move
    this.boundHideOnMapMove = this.hideOnMapMove.bind(this)
    window.addEventListener("region-map:moveend", this.boundHideOnMapMove)
  }

  disconnect() {
    this.cancelPendingSearch()
    window.removeEventListener("region-map:moveend", this.boundHideOnMapMove)
  }

  /**
   * Handle input changes (debounced)
   */
  onInput(event) {
    const query = event.target.value.trim()

    // Show/hide clear button
    this.toggleClearButton(query.length > 0)

    // Cancel pending search
    this.cancelPendingSearch()

    if (query.length === 0) {
      this.showTrendingOnly()
      return
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceValue)
  }

  /**
   * Handle input focus
   */
  onFocus() {
    const query = this.inputTarget.value.trim()

    if (query.length === 0) {
      this.showTrendingOnly()
    } else if (this.results.length > 0) {
      this.showDropdown()
    }
  }

  /**
   * Handle input blur (delayed to allow click on results)
   */
  onBlur() {
    setTimeout(() => {
      this.hideDropdown()
    }, 200)
  }

  /**
   * Handle keyboard navigation
   */
  onKeydown(event) {
    const items = this.getVisibleItems()
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateSelection(items)
        break

      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection(items)
        break

      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          items[this.selectedIndex].click()
        }
        break

      case "Escape":
        this.hideDropdown()
        this.inputTarget.blur()
        break
    }
  }

  /**
   * Clear search input
   */
  clearInput() {
    this.inputTarget.value = ""
    this.toggleClearButton(false)
    this.hideDropdown()
    this.inputTarget.focus()
  }

  /**
   * Perform API search
   */
  async performSearch(query) {
    this.showLoading()

    // Cancel any pending request
    this.cancelPendingSearch()
    this.abortController = new AbortController()

    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const json = await response.json()
      this.results = json.data || []

      this.hideLoading()
      this.renderResults()
    } catch (error) {
      if (error.name === "AbortError") {
        // Request was cancelled, ignore
        return
      }
      console.error("[DistrictAutocomplete] Search failed:", error)
      this.hideLoading()
      this.showNoResults()
    }
  }

  /**
   * Render search results
   */
  renderResults() {
    if (this.results.length === 0) {
      this.showNoResults()
      return
    }

    this.selectedIndex = -1
    this.resultsListTarget.innerHTML = this.results.map((district, index) =>
      this.renderResultItem(district, index)
    ).join("")

    this.hideTrendingSection()
    this.hideNoResults()
    this.showDropdown()
  }

  /**
   * Render a single result item
   */
  renderResultItem(district, index) {
    return `
      <li class="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-charcoal-50 transition-colors"
          data-index="${index}"
          data-district-id="${district.id}"
          data-district-name="${this.escapeHtml(district.name)}"
          data-district-city="${this.escapeHtml(district.city || '')}"
          data-district-lat="${district.latitude}"
          data-district-lng="${district.longitude}"
          data-action="click->district-autocomplete#selectResult">
        <svg class="w-5 h-5 text-charcoal-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
        <div class="flex-1 min-w-0">
          <div class="font-medium text-charcoal-800 truncate">${this.escapeHtml(district.name)}</div>
          ${district.city ? `<div class="text-sm text-charcoal-500 truncate">${this.escapeHtml(district.city)}</div>` : ""}
        </div>
      </li>
    `
  }

  /**
   * Escape a dynamic string before interpolating it into innerHTML (XSS defense-in-depth).
   * Gazetteer place names are trusted government data today, but this hardens the render path so a
   * future user-contributed name source can't inject markup. Used for every dynamic value above.
   */
  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[ch]))
  }

  /**
   * Handle result selection
   */
  selectResult(event) {
    const item = event.currentTarget
    const district = {
      id: parseInt(item.dataset.districtId, 10),
      name: item.dataset.districtName,
      city: item.dataset.districtCity,
      lat: parseFloat(item.dataset.districtLat),
      lng: parseFloat(item.dataset.districtLng)
    }

    // Update input
    this.inputTarget.value = `${district.city} ${district.name}`.trim()
    this.toggleClearButton(true)

    // Dispatch custom event for placeholder visibility update (category-rotator)
    // Using custom event instead of "input" to avoid triggering search
    this.inputTarget.dispatchEvent(
      new CustomEvent("autocomplete:selected", { bubbles: true })
    )

    // Hide dropdown
    this.hideDropdown()

    // Dispatch event for map integration
    this.dispatch("select", { detail: district })

    // Also dispatch directly to window for region-map controller
    window.dispatchEvent(new CustomEvent("district-autocomplete:select", {
      detail: district
    }))
  }

  /**
   * Fetch trending districts from API
   * Dispatches "trendingLoaded" event for sibling controllers (e.g., category-rotator)
   */
  async fetchTrendingDistricts() {
    if (!this.trendingUrlValue) return

    try {
      const response = await fetch(this.trendingUrlValue, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const json = await response.json()
      if (!Array.isArray(json.data)) {
        console.warn("[DistrictAutocomplete] Unexpected API response format")
        return
      }

      this.trendingDistricts = json.data

      // Dispatch event for sibling controllers (e.g., category-rotator)
      // Uses Stimulus dispatch() which bubbles up and includes controller identifier
      this.dispatch("trendingLoaded", {
        detail: { names: this.trendingDistricts.map(d => d.name) }
      })
    } catch (error) {
      console.error("[DistrictAutocomplete] Failed to fetch trending:", error)
    }
  }

  /**
   * Show trending districts dropdown
   */
  showTrendingOnly() {
    const hasTrending = this.trendingDistricts.length > 0

    if (!hasTrending) {
      this.hideDropdown()
      return
    }

    // Render trending districts
    if (this.hasTrendingSectionTarget && this.hasTrendingListTarget) {
      this.trendingListTarget.innerHTML = this.trendingDistricts.map((district) =>
        this.renderTrendingItem(district)
      ).join("")
      this.trendingSectionTarget.classList.remove("hidden")
    }

    this.hideLoading()
    this.hideNoResults()
    this.resultsListTarget.innerHTML = ""
    this.showDropdown()
  }

  /**
   * Render a trending district item with ranking badge
   * @param {Object} district - District object with rank, name, city, latitude, longitude
   */
  renderTrendingItem(district) {
    const badgeClasses = this.getRankBadgeClasses(district.rank)

    return `
      <li class="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-charcoal-50 transition-colors"
          data-district-id="${district.id}"
          data-district-name="${this.escapeHtml(district.name)}"
          data-district-city="${this.escapeHtml(district.city || '')}"
          data-district-lat="${district.latitude}"
          data-district-lng="${district.longitude}"
          data-action="click->district-autocomplete#selectResult">
        <span class="w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${badgeClasses}">
          ${district.rank}
        </span>
        <div class="flex-1 min-w-0">
          <div class="font-medium text-charcoal-800 truncate">${this.escapeHtml(district.name)}</div>
          ${district.city ? `<div class="text-sm text-charcoal-500 truncate">${this.escapeHtml(district.city)}</div>` : ""}
        </div>
        <svg class="w-4 h-4 text-crust-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </li>
    `
  }

  /**
   * Get Tailwind classes for ranking badge based on rank
   * @param {number} rank - District rank (1-5)
   * @returns {string} Tailwind classes for badge
   */
  getRankBadgeClasses(rank) {
    switch (rank) {
      case 1:
        return "bg-crust-500 text-white"
      case 2:
        return "bg-crust-400 text-white"
      case 3:
        return "bg-crust-300 text-charcoal-800"
      default:
        return "bg-charcoal-200 text-charcoal-700"
    }
  }

  /**
   * Get visible result/trending items
   */
  getVisibleItems() {
    const resultsItems = Array.from(this.resultsListTarget.querySelectorAll("li"))
    const trendingItems = (this.hasTrendingSectionTarget && !this.trendingSectionTarget.classList.contains("hidden"))
      ? Array.from(this.trendingListTarget.querySelectorAll("li"))
      : []
    return [...trendingItems, ...resultsItems]
  }

  /**
   * Update keyboard selection highlight
   */
  updateSelection(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("bg-charcoal-100")
      } else {
        item.classList.remove("bg-charcoal-100")
      }
    })
  }

  /**
   * Cancel pending search request
   */
  cancelPendingSearch() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  // UI helpers
  toggleClearButton(show) {
    this.clearButtonTarget.classList.toggle("hidden", !show)
  }

  showDropdown() {
    this.dropdownTarget.classList.remove("hidden")

    // Dispatch event for other controllers (e.g., region-map sidebar)
    window.dispatchEvent(new CustomEvent("district-autocomplete:dropdown-shown"))
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.selectedIndex = -1
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.hideNoResults()
    this.hideTrendingSection()
    this.resultsListTarget.innerHTML = ""
    this.showDropdown()
  }

  hideLoading() {
    this.loadingTarget.classList.add("hidden")
  }

  showNoResults() {
    this.noResultsTarget.classList.remove("hidden")
    this.hideTrendingSection()
    this.resultsListTarget.innerHTML = ""
    this.showDropdown()
  }

  hideNoResults() {
    this.noResultsTarget.classList.add("hidden")
  }

  hideTrendingSection() {
    if (this.hasTrendingSectionTarget) {
      this.trendingSectionTarget.classList.add("hidden")
    }
  }

  /**
   * Hide dropdown when map is moved
   */
  hideOnMapMove() {
    if (!this.dropdownTarget.classList.contains("hidden")) {
      this.hideDropdown()
      this.inputTarget.blur()
    }
  }
}
